import React, { useMemo, memo } from 'react';
import { Link } from '@plone/components';
import { Tag } from '@designsystem/stories/02-atoms/Tag';
import { IrfAlertMessages } from './index';
import { IrfMessages as messages } from './IrfMessages';
import { InternalIrfPublicationProps } from './utils/fetchData';
import { useIntl } from 'react-intl';
import { getOrderedTypeCodes, getGroupTitleMessageKey } from './IrfConfig';
import { sortEntriesByCitationDate } from './utils/sortByCitationDate';
import { Card } from '@designsystem/stories/03-molecules/Card';
import { sanitize } from '@designsystem/utils/sanitize';

/**
 * Processes IEEE citation entries to replace static [1] with consecutive numbering
 * @param citationEntry - The HTML citation entry from the API
 * @param index - The zero-based index for consecutive numbering
 * @returns Processed citation entry with correct IEEE numbering
 */
const processIEEECitation = (citationEntry: string, index: number): string => {
  if (!citationEntry || !citationEntry.includes('csl-left-margin')) {
    return citationEntry;
  }

  // Replace [1] in IEEE citations with consecutive numbering
  return citationEntry.replace(
    /(<div class="csl-left-margin">)\[(\d+)\](<\/div>)/,
    `$1[${index + 1}]$3`,
  );
};

interface RenderEntriesProps {
  entries: InternalIrfPublicationProps[];
  peerReviewedCache: Record<string, boolean>;
  activeTab: string;
  showOnlyPeerReviewed: boolean;
  getExcludedPublications: string[];
  isEditMode: boolean;
  dataState: string;
  jsonData: InternalIrfPublicationProps[];
  displayContentGrouped: string;
}

/**
 * The ungrouped list, rendered in full.
 *
 * It used to grow ten entries at a time behind an IntersectionObserver, which
 * bought nothing: `@irf_publications` has no paging, so the whole list is
 * already in memory by the time anything renders — only the cards were held
 * back. It cost a lot, though: the sentinel re-entered the viewport whenever
 * the surrounding container grew (an accordion panel above all), appended the
 * next ten and moved the page under the reader, while entries past the tenth
 * existed in no DOM that find-in-page, printing or a deep link could reach.
 * The grouped branch below has always rendered everything at once. #1700
 */
const NonGroupedContent: React.FC<{
  entries: InternalIrfPublicationProps[];
  displayContentGrouped: string;
}> = memo(({ entries, displayContentGrouped }) => (
  <>
    {entries.map((entry, index) => (
      <EntryItem
        key={`${entry.url_handle}-${index}`}
        entry={entry}
        showType={displayContentGrouped === 'group_by_type'}
        index={index}
      />
    ))}
  </>
));

NonGroupedContent.displayName = 'NonGroupedContent';

interface EntryItemProps {
  entry: InternalIrfPublicationProps;
  showType: boolean;
  index?: number;
}

const EntryItem: React.FC<EntryItemProps> = memo(
  ({ entry, showType, index = 0 }) => {
    const entryType = entry.type?.split('-').slice(1).join('').trim() || '';
    const intl = useIntl();

    // Process IEEE citation numbering if needed
    const processedCitation = useMemo(() => {
      return processIEEECitation(entry.citation_entry || '', index);
    }, [entry.citation_entry, index]);

    return (
      <Card
        className="listing__item"
        type="publication"
        title={(showType && entryType) || ''}
      >
        <div
          className="card__description"
          dangerouslySetInnerHTML={{
            __html: sanitize(processedCitation),
          }}
        />
        <div className="irf__additions">
          {entry.peer_reviewed && (
            <Tag label={entry.peer_reviewed ? 'Peer-Reviewed' : ''} />
          )}

          <Link
            href={entry.url_handle}
            target="_blank"
            className="btn btn--tertiary btn--medium card__readmore btn--icon-after"
          >
            <i
              className="icn icn--arrow_right_triangle btn__icon"
              data-icon=""
            />
            {intl.formatMessage(messages.moreLink)}
          </Link>
        </div>
      </Card>
    );
  },
);

export const RenderEntries: React.FC<RenderEntriesProps> = memo(
  ({
    entries,
    peerReviewedCache,
    activeTab,
    showOnlyPeerReviewed,
    getExcludedPublications,
    isEditMode,
    dataState,
    jsonData,
    displayContentGrouped,
  }) => {
    const intl = useIntl();

    const filteredAndGroupedEntries = useMemo(() => {
      const filteredEntries = entries.filter(
        (entry) =>
          (!peerReviewedCache[activeTab] || entry.peer_reviewed === true) &&
          (activeTab === 'projectTab' ||
            !showOnlyPeerReviewed ||
            entry.peer_reviewed) &&
          !getExcludedPublications.includes(entry.url_handle?.toString() || ''),
      );

      // Sort by the date shown in the citation so that – within each year and
      // group – concrete month/day entries come before bare-year ones and
      // undated entries sort to the end (#1618). Grouping below preserves this
      // order, so both the grouped and non-grouped views stay chronological.
      const filtered = sortEntriesByCitationDate(filteredEntries);

      if (displayContentGrouped === 'group_by_type') {
        // Group entries directly by message key to preserve chronological order
        // This ensures that entries with different type codes but same message key
        // (e.g., 04A and 04B both map to "BookParts") remain in chronological order
        const groupedByMessageKey: Record<
          string,
          { entries: InternalIrfPublicationProps[]; typeCode: string }
        > = {};

        // Iterate through filtered entries (already in chronological order from API)
        filtered.forEach((entry) => {
          const typeCode = entry.type?.split('-')[0]?.trim() || '';
          const messageKey = getGroupTitleMessageKey(typeCode);

          if (!groupedByMessageKey[messageKey]) {
            groupedByMessageKey[messageKey] = {
              entries: [],
              typeCode: typeCode, // Use the first typeCode encountered for this message key
            };
          }
          // Add entry while preserving original order
          groupedByMessageKey[messageKey].entries.push(entry);
        });

        // Order the groups according to IrfConfig priority
        const orderedTypeCodes = getOrderedTypeCodes();
        const orderedGroupedByMessageKey: Record<
          string,
          { entries: InternalIrfPublicationProps[]; typeCode: string }
        > = {};

        // Add groups in the order defined by IrfConfig
        orderedTypeCodes.forEach((typeCode) => {
          const messageKey = getGroupTitleMessageKey(typeCode);
          // Only add each message key once (first occurrence in orderedTypeCodes)
          if (
            groupedByMessageKey[messageKey] &&
            !orderedGroupedByMessageKey[messageKey]
          ) {
            orderedGroupedByMessageKey[messageKey] =
              groupedByMessageKey[messageKey];
          }
        });

        // Add any remaining groups that weren't in the config (fallback)
        Object.entries(groupedByMessageKey).forEach(([messageKey, data]) => {
          if (!orderedGroupedByMessageKey[messageKey]) {
            orderedGroupedByMessageKey[messageKey] = data;
          }
        });

        return { filtered, grouped: orderedGroupedByMessageKey };
      }

      return { filtered, grouped: null };
    }, [
      entries,
      peerReviewedCache,
      activeTab,
      showOnlyPeerReviewed,
      getExcludedPublications,
      displayContentGrouped,
    ]);

    if (filteredAndGroupedEntries.filtered.length === 0) {
      return <IrfAlertMessages messageType="noPeerReview" />;
    }

    return (
      <>
        {isEditMode === true && dataState === 'utd' ? (
          <IrfAlertMessages
            dataState={dataState}
            totalEntrys={jsonData.length}
          />
        ) : (
          <>
            {isEditMode === true && <IrfAlertMessages dataState={dataState} />}
          </>
        )}

        {/* Grouped content */}
        {displayContentGrouped === 'group_by_type' &&
          filteredAndGroupedEntries.grouped &&
          (() => {
            let globalIndex = 0; // Track consecutive numbering across all groups
            return Object.entries(filteredAndGroupedEntries.grouped).map(
              ([messageKey, groupData]) => {
                const typedGroupData = groupData as {
                  entries: InternalIrfPublicationProps[];
                  typeCode: string;
                };
                const groupTitle =
                  messageKey && (messages as any)[messageKey]
                    ? intl.formatMessage((messages as any)[messageKey])
                    : messageKey; // Fallback to message key

                // Skip group titles for Projects and Presentations
                const shouldShowGroupTitle =
                  messageKey !== 'Projects' && messageKey !== 'Presentations';

                return (
                  <React.Fragment key={messageKey}>
                    {shouldShowGroupTitle && <h3>{groupTitle}</h3>}
                    {typedGroupData.entries.map((entry, localIndex) => {
                      const currentGlobalIndex = globalIndex++;
                      return (
                        <EntryItem
                          key={`${entry.url_handle}-${localIndex}`}
                          entry={entry}
                          showType={false}
                          index={currentGlobalIndex}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              },
            );
          })()}

        {displayContentGrouped !== 'group_by_type' && (
          <NonGroupedContent
            entries={filteredAndGroupedEntries.filtered}
            displayContentGrouped={displayContentGrouped}
          />
        )}
      </>
    );
  },
);

RenderEntries.displayName = 'RenderEntries';
EntryItem.displayName = 'EntryItem';
